import { useState, useCallback } from "react";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";
import RichText from "@/components/base/RichText";
import AppendableBlocks from "@/components/feature/AppendableBlocks";
import { storeFormEntry } from "@/utils/formEntryStore";
import { submitFormToSupabase } from "@/utils/submitFormToSupabase";
import { defaultContact, type ContactInfo, type SocialBubble, type InfoCard, type FormField } from "@/data/siteContent";

// Google Form submission endpoint (no CORS issues when using no-cors fetch or hidden iframe)
// Form: https://docs.google.com/forms/d/e/1FAIpQLSd99Xgq42UR7Fyb3DYF9FNx1dh59mwYudfG2O4QLK_B5ToBbQ/viewform
const GOOGLE_FORM_ACTION = "https://docs.google.com/forms/d/e/1FAIpQLSd99Xgq42UR7Fyb3DYF9FNx1dh59mwYudfG2O4QLK_B5ToBbQ/formResponse";

// Google Form field entry IDs — find these by opening your form in edit mode,
// clicking ⋮ > "Get pre-filled link", filling dummy values, then reading the URL.
// Example: &entry.123456789=YourValue  →  field ID is 123456789
// UPDATE THESE with your actual entry IDs from the Google Form:
const GOOGLE_FIELD_IDS = {
  name:    "entry.144823886",   // "Your Name" field
  email:   "entry.1187585918",   // "Email Address" field
  subject: "entry.294433892",   // "Subject" field
  phone:   "entry.892272833",   // "Phone Number" field
  message: "entry.384333845",    // "Message" field
};



const ICON_OPTIONS = [
  "ri-instagram-line", "ri-tiktok-line", "ri-mail-line", "ri-facebook-line",
  "ri-twitter-x-line", "ri-youtube-line", "ri-pinterest-line", "ri-linkedin-line",
  "ri-phone-line", "ri-whatsapp-line", "ri-telegram-line", "ri-snapchat-line",
  "ri-global-line", "ri-store-2-line", "ri-heart-line",
];

const CARD_ICON_OPTIONS = [
  "ri-map-pin-line", "ri-time-line", "ri-mail-line", "ri-phone-line",
  "ri-store-2-line", "ri-information-line", "ri-calendar-line", "ri-car-line",
  "ri-parking-line", "ri-building-line", "ri-home-line", "ri-global-line",
];

const CARD_COLOR_OPTIONS = [
  { iconBg: "bg-pink-100", iconColor: "text-pink-500", label: "Pink" },
  { iconBg: "bg-green-100", iconColor: "text-green-600", label: "Green" },
  { iconBg: "bg-amber-100", iconColor: "text-amber-600", label: "Amber" },
  { iconBg: "bg-sky-100", iconColor: "text-sky-600", label: "Sky" },
];

interface ContactSectionProps { dataKey?: string; }

export default function ContactSection({ dataKey }: ContactSectionProps = {}) {
  const { isEditing: isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const [info, setInfo] = useAdminStorage<ContactInfo>(dataKey ?? "ma_contact_v3", defaultContact);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);
  const [editingSocialId, setEditingSocialId] = useState<string | null>(null);
  const [socialDraft, setSocialDraft] = useState<Partial<SocialBubble>>({});
  const [addingSocial, setAddingSocial] = useState(false);
  const [newSocial, setNewSocial] = useState<Partial<SocialBubble>>({ icon: "ri-global-line", label: "", href: "" });
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [cardDraft, setCardDraft] = useState<Partial<InfoCard>>({});
  const [addingCard, setAddingCard] = useState(false);
  const [newCard, setNewCard] = useState<Partial<InfoCard>>({ icon: "ri-information-line", iconBg: "bg-pink-100", iconColor: "text-pink-500", title: "New Info", lines: ["Click to edit"] });

  const set = useCallback(<K extends keyof ContactInfo>(key: K, val: ContactInfo[K]) => {
    setInfo({ ...info, [key]: val });
  }, [info, setInfo]);

  const updateField = useCallback((key: keyof ContactInfo["fields"], part: "label" | "placeholder", val: string) => {
    setInfo({ ...info, fields: { ...info.fields, [key]: { ...info.fields[key], [part]: val } } });
  }, [info, setInfo]);

  const removeSocial = useCallback((id: string) => {
    setInfo({ ...info, socialLinks: info.socialLinks.filter((s) => s.id !== id) });
  }, [info, setInfo]);

  const saveSocialEdit = useCallback(() => {
    if (!editingSocialId) return;
    setInfo({ ...info, socialLinks: info.socialLinks.map((s) => s.id === editingSocialId ? { ...s, ...socialDraft } as SocialBubble : s) });
    setEditingSocialId(null);
    setSocialDraft({});
  }, [info, setInfo, editingSocialId, socialDraft]);

  const addSocial = useCallback(() => {
    if (!newSocial.label || !newSocial.href) return;
    const id = `social-${Date.now()}`;
    setInfo({ ...info, socialLinks: [...info.socialLinks, { id, icon: newSocial.icon || "ri-global-line", label: newSocial.label!, href: newSocial.href! }] });
    setNewSocial({ icon: "ri-global-line", label: "", href: "" });
    setAddingSocial(false);
  }, [info, setInfo, newSocial]);

  const updateCardLine = useCallback((cardId: string, lineIdx: number, val: string) => {
    const infoCards = info.infoCards.map((c) => {
      if (c.id !== cardId) return c;
      const lines = c.lines.map((l, i) => i === lineIdx ? val : l);
      return { ...c, lines };
    });
    setInfo({ ...info, infoCards });
  }, [info, setInfo]);

  const addCardLine = useCallback((cardId: string) => {
    const infoCards = info.infoCards.map((c) => c.id === cardId ? { ...c, lines: [...c.lines, "New line"] } : c);
    setInfo({ ...info, infoCards });
  }, [info, setInfo]);

  const removeCardLine = useCallback((cardId: string, lineIdx: number) => {
    const infoCards = info.infoCards.map((c) => c.id === cardId ? { ...c, lines: c.lines.filter((_, i) => i !== lineIdx) } : c);
    setInfo({ ...info, infoCards });
  }, [info, setInfo]);

  const removeCard = useCallback((cardId: string) => {
    setInfo({ ...info, infoCards: info.infoCards.filter((c) => c.id !== cardId) });
  }, [info, setInfo]);

  const saveCardEdit = useCallback(() => {
    if (!editingCardId) return;
    setInfo({ ...info, infoCards: info.infoCards.map((c) => c.id === editingCardId ? { ...c, ...cardDraft } as InfoCard : c) });
    setEditingCardId(null);
    setCardDraft({});
  }, [info, setInfo, editingCardId, cardDraft]);

  const addCard = useCallback(() => {
    if (!newCard.title) return;
    const id = `card-${Date.now()}`;
    setInfo({
      ...info,
      infoCards: [...info.infoCards, {
        id,
        icon: newCard.icon || "ri-information-line",
        iconBg: newCard.iconBg || "bg-pink-100",
        iconColor: newCard.iconColor || "text-pink-500",
        title: newCard.title!,
        lines: newCard.lines || ["Click to edit"],
        visible: true,
      }],
    });
    setNewCard({ icon: "ri-information-line", iconBg: "bg-pink-100", iconColor: "text-pink-500", title: "New Info", lines: ["Click to edit"] });
    setAddingCard(false);
  }, [info, setInfo, newCard]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea[name='message']") as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setStatus("submitting");

    // Build Google Form payload using entry IDs
    const formData = new FormData(form);
    const googlePayload = new URLSearchParams();
    googlePayload.set(GOOGLE_FIELD_IDS.name,    formData.get("name")?.toString()    ?? "");
    googlePayload.set(GOOGLE_FIELD_IDS.email,   formData.get("email")?.toString()   ?? "");
    googlePayload.set(GOOGLE_FIELD_IDS.subject, formData.get("subject")?.toString() ?? "");
    googlePayload.set(GOOGLE_FIELD_IDS.phone,   formData.get("phone")?.toString()   ?? "");
    googlePayload.set(GOOGLE_FIELD_IDS.message, formData.get("message")?.toString() ?? "");

    const contactFields = [
      { label: "Name", value: formData.get("name")?.toString() ?? "" },
      { label: "Email", value: formData.get("email")?.toString() ?? "" },
      { label: "Subject", value: formData.get("subject")?.toString() ?? "" },
      { label: "Phone", value: formData.get("phone")?.toString() ?? "" },
      { label: "Message", value: formData.get("message")?.toString() ?? "" },
    ];
    // Store locally (fallback) AND sync to Supabase (primary)
    storeFormEntry("contact", "Send Us a Message", contactFields);
    submitFormToSupabase("contact", "Send Us a Message", contactFields).then((ok) => {
      if (!ok) console.error("[ContactSection] Form submission to Supabase failed");
    });

    try {
      // Google Forms doesn't support CORS, so we use no-cors mode.
      // The response will be opaque but the submission still goes through.
      await fetch(GOOGLE_FORM_ACTION, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: googlePayload.toString(),
      });
      // With no-cors we can't read the response, but if fetch didn't throw, it went through
      setStatus("success");
      form.reset();
      setCharCount(0);
    } catch {
      setStatus("error");
    }
  };

  const F = info.fields;
  const bg = isDark ? "bg-gray-900" : undefined;
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-gray-800" : "bg-white";
  const inputCls = isDark
    ? "w-full border border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
    : "w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 placeholder-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200";
  const labelCls = isDark ? "text-gray-300" : "text-gray-600";
  const formCardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100";
  const smallInputCls = "border border-gray-600 bg-gray-800 text-white rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-pink-400 w-full";

  return (
    <section id="contact" className={`py-24 px-6 transition-colors duration-300 ${bg ?? ""}`} style={isDark ? {} : { background: "#fff9fb" }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          {(isAdmin || info.subheading) && (
            isAdmin ? (
              <AdminEditField value={info.subheading} onSave={(v) => set("subheading", v)} onDelete={() => set("subheading", "")} inputClassName="text-green-500 text-sm font-bold uppercase tracking-widest text-center">
                <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{info.subheading}</span>
              </AdminEditField>
            ) : (
              <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{info.subheading}</span>
            )
          )}
          {(isAdmin || info.heading) && (
            isAdmin ? (
              <AdminEditField value={info.heading} onSave={(v) => set("heading", v)} onDelete={() => set("heading", "")} className="mt-3 mb-4" inputClassName="text-4xl font-extrabold text-gray-900 text-center">
                <h2 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{info.heading}</h2>
              </AdminEditField>
            ) : (
              <h2 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{info.heading}</h2>
            )
          )}
          {(isAdmin || info.intro) && (
            isAdmin ? (
              <AdminEditField value={info.intro} onSave={(v) => set("intro", v)} onDelete={() => set("intro", "")} multiline richText inputClassName="text-gray-500 text-lg text-center">
                <RichText html={info.intro} as="p" className={`text-lg max-w-xl mx-auto ${subColor}`} />
              </AdminEditField>
            ) : (
              <RichText html={info.intro} as="p" className={`text-lg max-w-xl mx-auto ${subColor}`} />
            )
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-10 items-start">
          {/* Contact Info Cards */}
          <div className="w-full lg:w-2/5 space-y-4">
            {info.infoCards.map((card) => (
              <div key={card.id} className={`flex items-start gap-4 rounded-2xl p-5 relative group/card ${cardBg}`}>
                {/* Icon — editable */}
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 relative group/icon ${card.iconBg}`}>
                  <i className={`${card.icon} ${card.iconColor} text-xl`}></i>
                  {isAdmin && (
                    <button
                      onClick={() => { setEditingCardId(card.id); setCardDraft({ ...card }); }}
                      className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl opacity-0 group-hover/icon:opacity-100 transition-opacity cursor-pointer"
                      title="Edit card"
                    >
                      <i className="ri-edit-line text-white text-xs"></i>
                    </button>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {isAdmin ? (
                    <AdminEditField value={card.title} onSave={(v) => {
                      const infoCards = info.infoCards.map((c) => c.id === card.id ? { ...c, title: v } : c);
                      setInfo({ ...info, infoCards });
                    }} className="mb-1" inputClassName="font-bold text-sm">
                      <div className={`font-bold text-sm mb-1 ${headingColor}`}>{card.title}</div>
                    </AdminEditField>
                  ) : (
                    <div className={`font-bold text-sm mb-1 ${headingColor}`}>{card.title}</div>
                  )}
                  <div className="space-y-0.5">
                    {card.lines.map((line, li) => (
                      <div key={li} className="flex items-center gap-1 group/line">
                        {isAdmin ? (
                          <>
                            <AdminEditField value={line} onSave={(v) => updateCardLine(card.id, li, v)} className="flex-1" inputClassName="text-sm text-gray-500">
                              <div className={`text-sm ${subColor}`}>{line}</div>
                            </AdminEditField>
                            <button onClick={() => removeCardLine(card.id, li)} className="w-4 h-4 flex items-center justify-center text-red-300 hover:text-red-500 cursor-pointer opacity-0 group-hover/line:opacity-100 transition-opacity flex-shrink-0">
                              <i className="ri-close-line text-xs"></i>
                            </button>
                          </>
                        ) : (
                          <div className={`text-sm ${subColor}`}>{line}</div>
                        )}
                      </div>
                    ))}
                    {isAdmin && (
                      <button onClick={() => addCardLine(card.id)} className="text-xs text-pink-400 hover:text-pink-600 flex items-center gap-1 cursor-pointer mt-1">
                        <i className="ri-add-line"></i> Add line
                      </button>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => removeCard(card.id)}
                    className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-red-300 hover:text-red-500 cursor-pointer rounded-full hover:bg-red-50 opacity-0 group-hover/card:opacity-100 transition-opacity"
                    title="Remove card"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                )}
              </div>
            ))}

            {/* Add new card (admin) */}
            {isAdmin && !addingCard && (
              <button
                onClick={() => setAddingCard(true)}
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-pink-200 hover:border-pink-400 text-pink-400 hover:text-pink-500 rounded-2xl py-4 text-sm font-semibold transition-colors cursor-pointer"
              >
                <i className="ri-add-line text-lg"></i>
                Add Info Card
              </button>
            )}
            {isAdmin && addingCard && (
              <div className="bg-gray-900 rounded-2xl p-4 space-y-2">
                <p className="text-white text-xs font-bold">New Info Card</p>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Icon</label>
                  <div className="flex flex-wrap gap-1.5">
                    {CARD_ICON_OPTIONS.map((ic) => (
                      <button key={ic} onClick={() => setNewCard({ ...newCard, icon: ic })} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${newCard.icon === ic ? "bg-pink-500 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}>
                        <i className={`${ic} text-sm`}></i>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Color</label>
                  <div className="flex gap-2">
                    {CARD_COLOR_OPTIONS.map((c) => (
                      <button key={c.label} onClick={() => setNewCard({ ...newCard, iconBg: c.iconBg, iconColor: c.iconColor })} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer border-2 transition-colors ${newCard.iconBg === c.iconBg ? "border-white" : "border-transparent"} ${c.iconBg}`}>
                        <i className={`${newCard.icon || "ri-information-line"} ${c.iconColor} text-sm`}></i>
                      </button>
                    ))}
                  </div>
                </div>
                <input type="text" placeholder="Card title (e.g. Phone)" value={newCard.title || ""} onChange={(e) => setNewCard({ ...newCard, title: e.target.value })} className={smallInputCls} />
                <div className="flex gap-2">
                  <button onClick={addCard} className="bg-green-500 hover:bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap">Add</button>
                  <button onClick={() => setAddingCard(false)} className="text-gray-400 text-xs cursor-pointer whitespace-nowrap">Cancel</button>
                </div>
              </div>
            )}

            {/* Follow Along */}
            <div className={`rounded-2xl p-5 ${cardBg}`}>
              {isAdmin ? (
                <AdminEditField value={info.followAlongLabel} onSave={(v) => set("followAlongLabel", v)} className="mb-3" inputClassName="text-xs uppercase tracking-widest font-semibold text-gray-400">
                  <p className="text-gray-400 text-xs uppercase tracking-widest mb-3 font-semibold">{info.followAlongLabel}</p>
                </AdminEditField>
              ) : (
                <p className="text-gray-400 text-xs uppercase tracking-widest mb-3 font-semibold">{info.followAlongLabel}</p>
              )}

              <div className="flex gap-3 flex-wrap">
                {info.socialLinks.map((s) => (
                  <div key={s.id} className="relative group">
                    <a
                      href={s.href}
                      aria-label={s.label}
                      onClick={(e) => isAdmin && e.preventDefault()}
                      className="w-10 h-10 flex items-center justify-center bg-pink-50 hover:bg-pink-500 rounded-full text-pink-400 hover:text-white transition-colors cursor-pointer"
                    >
                      <i className={`${s.icon} text-lg`}></i>
                    </a>
                    {isAdmin && (
                      <div className="absolute -top-1 -right-1 hidden group-hover:flex gap-0.5 z-10">
                        <button onClick={() => { setEditingSocialId(s.id); setSocialDraft({ ...s }); }} className="w-4 h-4 flex items-center justify-center bg-pink-500 text-white rounded-full cursor-pointer" title="Edit">
                          <i className="ri-edit-line" style={{ fontSize: "8px" }}></i>
                        </button>
                        <button onClick={() => removeSocial(s.id)} className="w-4 h-4 flex items-center justify-center bg-red-500 text-white rounded-full cursor-pointer" title="Remove">
                          <i className="ri-close-line" style={{ fontSize: "8px" }}></i>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {isAdmin && !addingSocial && (
                  <button onClick={() => setAddingSocial(true)} className="w-10 h-10 flex items-center justify-center border-2 border-dashed border-pink-300 rounded-full text-pink-400 hover:border-pink-500 hover:text-pink-600 cursor-pointer transition-colors" title="Add social link">
                    <i className="ri-add-line text-lg"></i>
                  </button>
                )}
              </div>

              {isAdmin && addingSocial && (
                <div className="mt-3 space-y-2 bg-gray-900 rounded-xl p-3">
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Icon</label>
                    <div className="flex flex-wrap gap-1.5">
                      {ICON_OPTIONS.map((ic) => (
                        <button key={ic} onClick={() => setNewSocial({ ...newSocial, icon: ic })} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${newSocial.icon === ic ? "bg-pink-500 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}>
                          <i className={`${ic} text-sm`}></i>
                        </button>
                      ))}
                    </div>
                  </div>
                  <input type="text" placeholder="Label (e.g. Instagram)" value={newSocial.label || ""} onChange={(e) => setNewSocial({ ...newSocial, label: e.target.value })} className={smallInputCls} />
                  <input type="text" placeholder="URL (e.g. https://instagram.com/...)" value={newSocial.href || ""} onChange={(e) => setNewSocial({ ...newSocial, href: e.target.value })} className={smallInputCls} />
                  <div className="flex gap-2">
                    <button onClick={addSocial} className="bg-green-500 hover:bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap">Add</button>
                    <button onClick={() => setAddingSocial(false)} className="text-gray-400 text-xs cursor-pointer whitespace-nowrap">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Form */}
          <div className={`w-full lg:w-3/5 rounded-3xl p-8 border ${formCardBg}`}>
            {status === "success" ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-green-500 text-3xl"></i>
                </div>
                {isAdmin ? (
                  <>
                    <AdminEditField value={info.successTitle} onSave={(v) => set("successTitle", v)} className="mb-2" inputClassName="text-2xl font-bold text-gray-900 text-center">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{info.successTitle}</h3>
                    </AdminEditField>
                    <AdminEditField value={info.successBody} onSave={(v) => set("successBody", v)} multiline inputClassName="text-gray-500 text-center">
                      <p className="text-gray-500">{info.successBody}</p>
                    </AdminEditField>
                  </>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{info.successTitle}</h3>
                    <p className="text-gray-500">{info.successBody}</p>
                  </>
                )}
                <button onClick={() => setStatus("idle")} className="mt-6 bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-3 rounded-full cursor-pointer whitespace-nowrap">
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} id="contact-form" className="space-y-4">
                {isAdmin ? (
                  <AdminEditField value={info.formTitle} onSave={(v) => set("formTitle", v)} className="mb-5" inputClassName="text-gray-900 font-bold text-xl">
                    <h3 className={`font-bold text-xl mb-5 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{info.formTitle}</h3>
                  </AdminEditField>
                ) : (
                  <h3 className={`font-bold text-xl mb-5 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{info.formTitle}</h3>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    {isAdmin ? (
                      <AdminEditField value={F.name.label} onSave={(v) => updateField("name", "label", v)} className="mb-1.5" inputClassName="text-sm font-semibold text-gray-600">
                        <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.name.label}</label>
                      </AdminEditField>
                    ) : (
                      <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.name.label}</label>
                    )}
                    <input type="text" name="name" required placeholder={F.name.placeholder} className={inputCls} />
                  </div>
                  <div>
                    {isAdmin ? (
                      <AdminEditField value={F.email.label} onSave={(v) => updateField("email", "label", v)} className="mb-1.5" inputClassName="text-sm font-semibold text-gray-600">
                        <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.email.label}</label>
                      </AdminEditField>
                    ) : (
                      <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.email.label}</label>
                    )}
                    <input type="email" name="email" required placeholder={F.email.placeholder} className={inputCls} />
                    {/* Email helper text */}
                    {isAdmin ? (
                      <AdminEditField value={info.emailHelperText} onSave={(v) => set("emailHelperText", v)} className="mt-1" inputClassName="text-xs text-gray-400">
                        <p className={`text-xs mt-1 ${subColor}`}>{info.emailHelperText}</p>
                      </AdminEditField>
                    ) : (
                      <p className={`text-xs mt-1 ${subColor}`}>{info.emailHelperText}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    {isAdmin ? (
                      <AdminEditField value={F.subject.label} onSave={(v) => updateField("subject", "label", v)} className="mb-1.5" inputClassName="text-sm font-semibold text-gray-600">
                        <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.subject.label}</label>
                      </AdminEditField>
                    ) : (
                      <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>{F.subject.label}</label>
                    )}
                    <input type="text" name="subject" required placeholder={F.subject.placeholder} className={inputCls} />
                  </div>
                  <div>
                    {isAdmin ? (
                      <AdminEditField value={F.phone?.label ?? "Phone Number"} onSave={(v) => updateField("phone" as keyof ContactInfo["fields"], "label", v)} className="mb-1.5" inputClassName="text-sm font-semibold text-gray-600">
                        <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>Phone Number</label>
                      </AdminEditField>
                    ) : (
                      <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>Phone Number</label>
                    )}
                    <input type="tel" name="phone" placeholder="(555) 000-0000" className={inputCls} />
                    {/* Phone helper text */}
                    {isAdmin ? (
                      <AdminEditField value={info.phoneHelperText} onSave={(v) => set("phoneHelperText", v)} className="mt-1" inputClassName="text-xs text-gray-400">
                        <p className={`text-xs mt-1 ${subColor}`}>{info.phoneHelperText}</p>
                      </AdminEditField>
                    ) : (
                      <p className={`text-xs mt-1 ${subColor}`}>{info.phoneHelperText}</p>
                    )}
                  </div>
                </div>
                <div>
                  {isAdmin ? (
                    <AdminEditField value={F.message.label} onSave={(v) => updateField("message", "label", v)} className="mb-1.5" inputClassName="text-sm font-semibold text-gray-600">
                      <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>
                        {F.message.label}{" "}
                        <span className={`font-normal ${charCount > 500 ? "text-red-400" : "text-gray-400"}`}>({charCount}/500)</span>
                      </label>
                    </AdminEditField>
                  ) : (
                    <label className={`block text-sm font-semibold mb-1.5 ${labelCls}`}>
                      {F.message.label}{" "}
                      <span className={`font-normal ${charCount > 500 ? "text-red-400" : "text-gray-400"}`}>({charCount}/500)</span>
                    </label>
                  )}
                  <textarea name="message" required rows={5} maxLength={500} placeholder={F.message.placeholder} className={`${inputCls} resize-none`} onChange={(e) => setCharCount(e.target.value.length)} />
                </div>
                {status === "error" && <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>}
                <button type="submit" disabled={status === "submitting" || charCount > 500} className="w-full bg-pink-500 hover:bg-pink-600 disabled:opacity-60 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap">
                  {status === "submitting" ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Sending...</>
                  ) : (
                    <>
                      {isAdmin ? (
                        <AdminEditField value={info.submitLabel} onSave={(v) => set("submitLabel", v)} inputClassName="text-white font-bold text-center">
                          <span>{info.submitLabel}</span>
                        </AdminEditField>
                      ) : (
                        <span>{info.submitLabel}</span>
                      )}
                      <i className="ri-send-plane-line"></i>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        <AppendableBlocks blocks={info.extraBlocks ?? []} onChange={(v) => set("extraBlocks", v)} className="mt-10" />
      </div>

      {/* Edit social modal */}
      {editingSocialId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setEditingSocialId(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-80 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold text-base">Edit Social Link</h3>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Icon</label>
              <div className="flex flex-wrap gap-1.5">
                {ICON_OPTIONS.map((ic) => (
                  <button key={ic} onClick={() => setSocialDraft({ ...socialDraft, icon: ic })} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${socialDraft.icon === ic ? "bg-pink-500 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}>
                    <i className={`${ic} text-sm`}></i>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Label</label>
              <input type="text" value={socialDraft.label || ""} onChange={(e) => setSocialDraft({ ...socialDraft, label: e.target.value })} className="border border-gray-600 bg-gray-800 text-white rounded-lg px-2 py-1 text-xs focus:outline-none w-full" />
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">URL</label>
              <input type="text" value={socialDraft.href || ""} onChange={(e) => setSocialDraft({ ...socialDraft, href: e.target.value })} className="border border-gray-600 bg-gray-800 text-white rounded-lg px-2 py-1 text-xs focus:outline-none w-full" />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={saveSocialEdit} className="bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap">Save</button>
              <button onClick={() => setEditingSocialId(null)} className="text-gray-400 hover:text-gray-200 text-sm cursor-pointer whitespace-nowrap">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit card icon/color modal */}
      {editingCardId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setEditingCardId(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-80 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold text-base">Edit Card Icon & Color</h3>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Icon</label>
              <div className="flex flex-wrap gap-1.5">
                {CARD_ICON_OPTIONS.map((ic) => (
                  <button key={ic} onClick={() => setCardDraft({ ...cardDraft, icon: ic })} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${cardDraft.icon === ic ? "bg-pink-500 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}>
                    <i className={`${ic} text-sm`}></i>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Color</label>
              <div className="flex gap-2">
                {CARD_COLOR_OPTIONS.map((c) => (
                  <button key={c.label} onClick={() => setCardDraft({ ...cardDraft, iconBg: c.iconBg, iconColor: c.iconColor })} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer border-2 transition-colors ${cardDraft.iconBg === c.iconBg ? "border-white" : "border-transparent"} ${c.iconBg}`}>
                    <i className={`${cardDraft.icon || "ri-information-line"} ${c.iconColor} text-sm`}></i>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={saveCardEdit} className="bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap">Save</button>
              <button onClick={() => setEditingCardId(null)} className="text-gray-400 hover:text-gray-200 text-sm cursor-pointer whitespace-nowrap">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
